/**
 * Bot Protection Utilities
 * Comprehensive security measures to prevent automated form abuse
 */
import { useState } from 'react';

// Rate limiting configuration
const RATE_LIMITS = {
  maxSubmissions: 1, // Maximum 1 submission per session
  maxSubmissionsPerDay: 2, // Maximum 2 submissions per day
  timeWindow: 24 * 60 * 60 * 1000, // 24 hours in milliseconds
  cooldownPeriod: 12 * 60 * 60 * 1000, // 12 hours cooldown after rate limit hit
  sessionWindow: 60 * 60 * 1000, // 1 hour session window
};

// Minimum time thresholds to detect too-fast submissions
const TIMING_THRESHOLDS = {
  minFormTime: 5000, // Minimum 5 seconds to fill form
  minTypingTime: 2000, // Minimum 2 seconds of typing activity
  maxIdleTime: 30 * 60 * 1000, // Maximum 30 minutes before form expires
};

// Suspicious patterns detection
const SUSPICIOUS_PATTERNS = {
  maxLength: 5000, // Maximum allowed message length
  spamKeywords: [
    'buy now', 'click here', 'make money', 'free money', 'earn cash',
    'viagra', 'casino', 'lottery', 'winner', 'congratulations',
    'urgent', 'act now', 'limited time', 'discount', 'offer expires',
    'bitcoin', 'crypto', 'investment opportunity', 'get rich',
    'work from home', 'mlm', 'pyramid', 'affiliate marketing'
  ],
  repetitiveChars: /(.)\1{10,}/, // 10+ consecutive same characters
  tooManyLinks: /https?:\/\/[^\s]+/g, // Multiple URLs pattern
  maxLinks: 2,
  suspiciousEmails: [
    '@tempmail', '@10minutemail', '@guerrillamail', '@mailinator',
    '@throwaway', '@temp-mail', '@sharklasers'
  ]
};

/**
 * Rate Limiting Manager
 */
class RateLimiter {
  constructor() {
    this.submissions = this.loadFromStorage('botProtection_submissions') || [];
    this.dailySubmissions = this.loadFromStorage('botProtection_dailySubmissions') || [];
    this.sessionSubmissions = this.loadFromStorage('botProtection_sessionSubmissions') || [];
    this.blocked = this.loadFromStorage('botProtection_blocked') || [];
    this.sessionStart = this.loadFromStorage('botProtection_sessionStart') || Date.now();
    
    // Save session start if it's a new session
    if (!this.loadFromStorage('botProtection_sessionStart')) {
      this.saveToStorage('botProtection_sessionStart', this.sessionStart);
    }
  }

  loadFromStorage(key) {
    try {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  }

  saveToStorage(key, data) {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch {
      // Silent fail if localStorage is not available
    }
  }

  cleanOldEntries() {
    const now = Date.now();
    
    // Clean old daily submissions (older than 24 hours)
    this.dailySubmissions = this.dailySubmissions.filter(
      timestamp => now - timestamp < RATE_LIMITS.timeWindow
    );
    
    // Clean old session submissions (check if session expired)
    const sessionExpired = now - this.sessionStart > RATE_LIMITS.sessionWindow;
    if (sessionExpired) {
      this.sessionSubmissions = [];
      this.sessionStart = now;
      this.saveToStorage('botProtection_sessionStart', this.sessionStart);
    }
    
    // Clean old blocked entries
    this.blocked = this.blocked.filter(
      timestamp => now - timestamp < RATE_LIMITS.cooldownPeriod
    );
    
    // Save cleaned data
    this.saveToStorage('botProtection_dailySubmissions', this.dailySubmissions);
    this.saveToStorage('botProtection_sessionSubmissions', this.sessionSubmissions);
    this.saveToStorage('botProtection_blocked', this.blocked);
  }

  isRateLimited() {
    this.cleanOldEntries();
    
    // Check if currently in cooldown period
    if (this.blocked.length > 0) {
      const lastBlocked = Math.max(...this.blocked);
      const remainingCooldown = RATE_LIMITS.cooldownPeriod - (Date.now() - lastBlocked);
      
      if (remainingCooldown > 0) {
        return {
          limited: true,
          reason: 'cooldown',
          remainingTime: remainingCooldown,
          message: 'Ya he recibido tu mensaje anterior, me pondré en contacto contigo a la brevedad.'
        };
      }
    }

    // Check daily submission limit
    if (this.dailySubmissions.length >= RATE_LIMITS.maxSubmissionsPerDay) {
      this.blocked.push(Date.now());
      this.saveToStorage('botProtection_blocked', this.blocked);
      
      return {
        limited: true,
        reason: 'daily_limit',
        remainingTime: RATE_LIMITS.cooldownPeriod,
        message: 'Ya he recibido tu mensaje anterior, me pondré en contacto contigo a la brevedad.'
      };
    }

    // Check session submission limit (1 per session)
    if (this.sessionSubmissions.length >= RATE_LIMITS.maxSubmissions) {
      return {
        limited: true,
        reason: 'session_limit',
        remainingTime: RATE_LIMITS.sessionWindow - (Date.now() - this.sessionStart),
        message: 'Ya he recibido tu mensaje anterior, me pondré en contacto contigo a la brevedad.'
      };
    }

    return { limited: false };
  }

  recordSubmission() {
    const now = Date.now();
    this.dailySubmissions.push(now);
    this.sessionSubmissions.push(now);
    this.saveToStorage('botProtection_dailySubmissions', this.dailySubmissions);
    this.saveToStorage('botProtection_sessionSubmissions', this.sessionSubmissions);
  }

  getRemainingSubmissions() {
    this.cleanOldEntries();
    
    // Check both daily and session limits
    const remainingDaily = Math.max(0, RATE_LIMITS.maxSubmissionsPerDay - this.dailySubmissions.length);
    const remainingSession = Math.max(0, RATE_LIMITS.maxSubmissions - this.sessionSubmissions.length);
    
    // Return the more restrictive limit
    return Math.min(remainingDaily, remainingSession);
  }

  getSubmissionStatus() {
    this.cleanOldEntries();
    
    const sessionTimeLeft = RATE_LIMITS.sessionWindow - (Date.now() - this.sessionStart);
    const hasSessionSubmission = this.sessionSubmissions.length > 0;
    const dailyCount = this.dailySubmissions.length;
    
    return {
      canSubmit: this.getRemainingSubmissions() > 0,
      dailyCount,
      maxDaily: RATE_LIMITS.maxSubmissionsPerDay,
      hasSessionSubmission,
      sessionTimeLeft: Math.max(0, sessionTimeLeft),
      nextSubmissionTime: hasSessionSubmission ? sessionTimeLeft : 0
    };
  }
}

/**
 * Form Timing Tracker
 */
class FormTimingTracker {
  constructor() {
    this.startTime = Date.now();
    this.lastActivity = Date.now();
    this.typingEvents = 0;
    this.mouseEvents = 0;
    this.keyboardEvents = 0;
  }

  recordActivity(type = 'general') {
    this.lastActivity = Date.now();
    
    switch (type) {
      case 'typing':
        this.typingEvents++;
        break;
      case 'mouse':
        this.mouseEvents++;
        break;
      case 'keyboard':
        this.keyboardEvents++;
        break;
      default:
        // General activity - no specific counter
        break;
    }
  }

  getFormTime() {
    return Date.now() - this.startTime;
  }

  getIdleTime() {
    return Date.now() - this.lastActivity;
  }

  isValidTiming() {
    const formTime = this.getFormTime();
    const idleTime = this.getIdleTime();
    
    return {
      valid: formTime >= TIMING_THRESHOLDS.minFormTime && 
             idleTime <= TIMING_THRESHOLDS.maxIdleTime &&
             this.typingEvents >= 5, // Minimum typing activity
      formTime,
      idleTime,
      activity: {
        typing: this.typingEvents,
        mouse: this.mouseEvents,
        keyboard: this.keyboardEvents
      }
    };
  }
}

/**
 * Content Analysis for Spam Detection
 */
export const analyzeContent = (formData) => {
  const issues = [];
  const { name, email, message, phone } = formData;

  // Check message length
  if (message && message.length > SUSPICIOUS_PATTERNS.maxLength) {
    issues.push({
      type: 'length',
      severity: 'high',
      message: 'Message too long'
    });
  }

  // Check for spam keywords
  const lowerMessage = (message || '').toLowerCase();
  const foundKeywords = SUSPICIOUS_PATTERNS.spamKeywords.filter(
    keyword => lowerMessage.includes(keyword)
  );
  
  if (foundKeywords.length > 0) {
    issues.push({
      type: 'spam_keywords',
      severity: foundKeywords.length > 2 ? 'high' : 'medium',
      message: `Contains suspicious keywords: ${foundKeywords.join(', ')}`
    });
  }

  // Check for repetitive characters
  if (SUSPICIOUS_PATTERNS.repetitiveChars.test(message || '')) {
    issues.push({
      type: 'repetitive',
      severity: 'medium',
      message: 'Contains repetitive characters'
    });
  }

  // Check for too many links
  const links = (message || '').match(SUSPICIOUS_PATTERNS.tooManyLinks) || [];
  if (links.length > SUSPICIOUS_PATTERNS.maxLinks) {
    issues.push({
      type: 'links',
      severity: 'high',
      message: `Contains too many links (${links.length})`
    });
  }

  // Check for suspicious email domains
  const suspiciousEmail = SUSPICIOUS_PATTERNS.suspiciousEmails.some(
    domain => (email || '').toLowerCase().includes(domain)
  );
  
  if (suspiciousEmail) {
    issues.push({
      type: 'temp_email',
      severity: 'high',
      message: 'Using temporary email service'
    });
  }

  // Check for generic/bot-like names
  if (name && /^(test|bot|spam|admin|user)\d*$/i.test(name.trim())) {
    issues.push({
      type: 'generic_name',
      severity: 'medium',
      message: 'Generic or bot-like name'
    });
  }

  // Check for phone number patterns (if provided)
  if (phone && /^(\d)\1+$/.test(phone.replace(/\D/g, ''))) {
    issues.push({
      type: 'fake_phone',
      severity: 'medium',
      message: 'Repetitive phone number pattern'
    });
  }

  return {
    score: calculateSuspicionScore(issues),
    issues,
    blocked: issues.some(issue => issue.severity === 'high')
  };
};

const calculateSuspicionScore = (issues) => {
  return issues.reduce((score, issue) => {
    switch (issue.severity) {
      case 'high': return score + 3;
      case 'medium': return score + 2;
      case 'low': return score + 1;
      default: return score;
    }
  }, 0);
};

/**
 * Honeypot Field Generator
 */
export const generateHoneypotFields = () => {
  const fields = [
    'website', 'url', 'homepage', 'company_name', 
    'business_email', 'fax', 'comment', 'additional_info'
  ];
  
  return fields[Math.floor(Math.random() * fields.length)];
};

/**
 * Main Bot Protection Hook
 */
export const useBotProtection = (formId = 'default') => {
  const [rateLimiter] = useState(() => new RateLimiter());
  const [timingTracker] = useState(() => new FormTimingTracker());
  const [honeypotField] = useState(() => generateHoneypotFields());

  // Activity tracking
  const recordActivity = (type) => {
    timingTracker.recordActivity(type);
  };

  // Comprehensive validation
  const validateSubmission = (formData) => {
    // Check rate limiting
    const rateLimitCheck = rateLimiter.isRateLimited();
    if (rateLimitCheck.limited) {
      return {
        allowed: false,
        reason: 'rate_limit',
        message: 'Ya he recibido tu mensaje anterior, me pondré en contacto contigo a la brevedad.',
        details: rateLimitCheck
      };
    }

    // Check honeypot
    if (formData[honeypotField] && formData[honeypotField].trim() !== '') {
      return {
        allowed: false,
        reason: 'honeypot',
        message: 'Envío sospechoso detectado.',
        details: { field: honeypotField }
      };
    }

    // Check timing
    const timingCheck = timingTracker.isValidTiming();
    if (!timingCheck.valid) {
      return {
        allowed: false,
        reason: 'timing',
        message: 'Por favor, tómate tu tiempo para completar el formulario.',
        details: timingCheck
      };
    }

    // Analyze content
    const contentAnalysis = analyzeContent(formData);
    if (contentAnalysis.blocked) {
      return {
        allowed: false,
        reason: 'content',
        message: 'Por favor, revisa el contenido de tu mensaje y proporciona información más detallada.',
        details: contentAnalysis
      };
    }

    // If all checks pass, record submission
    rateLimiter.recordSubmission();
    
    return {
      allowed: true,
      details: {
        timing: timingCheck,
        content: contentAnalysis,
        remainingSubmissions: rateLimiter.getRemainingSubmissions()
      }
    };
  };

  return {
    honeypotField,
    recordActivity,
    validateSubmission,
    getRemainingSubmissions: () => rateLimiter.getRemainingSubmissions(),
    getFormTime: () => timingTracker.getFormTime(),
    isRateLimited: () => rateLimiter.isRateLimited()
  };
};

/**
 * Enhanced Form Field Component with Activity Tracking
 */
export const withBotProtection = (WrappedComponent) => {
  return function BotProtectedComponent(props) {
    const { recordActivity } = props;
    
    const enhancedProps = {
      ...props,
      onFocus: (e) => {
        recordActivity?.('mouse');
        props.onFocus?.(e);
      },
      onKeyDown: (e) => {
        recordActivity?.('keyboard');
        props.onKeyDown?.(e);
      },
      onChange: (e) => {
        recordActivity?.('typing');
        props.onChange?.(e);
      },
      onMouseMove: (e) => {
        recordActivity?.('mouse');
        props.onMouseMove?.(e);
      }
    };

    return <WrappedComponent {...enhancedProps} />;
  };
};

const botProtection = {
  useBotProtection,
  analyzeContent,
  generateHoneypotFields,
  withBotProtection
};

export default botProtection;
